import React, { useEffect, useState } from 'react';
import { LuLoaderPinwheel, LuPencilLine, LuSparkles, LuTrash2, LuTriangleAlert, LuX } from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';

import { AdrMeta, AdrStatus, ProjectKnowledge, ProjectKnowledgeType } from '../../entities/project-knowledge';
import {
  deleteProjectKnowledge,
  knowledgeErrorMessage,
  listProjectKnowledge,
  upsertProjectKnowledge,
} from '../../shared/api/knowledge';
import { useProjectById } from '../../shared/contexts/Projects.provider';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import { formatRelativeTime } from '../../shared/utils/time';
import { TYPE_META } from '../../widgets/knowledge/type-meta';
import MarkdownRenderer from '../../widgets/MarkdownRenderer';
import { TaskDetailShell } from '../../widgets/task-detail/TaskDetailShell';
import { MarkdownField } from '../../widgets/ui/MarkdownField';

// Server-validated required structure for a new ADR's content (mflow knowledge base "ADR"
// contract, confirmed with the mflow-msa-mono session) — pre-filled so a new ADR doesn't get
// rejected on save for missing a section the user didn't know was mandatory.
const ADR_CONTENT_TEMPLATE = `## Context\n\nWhat is the issue we're facing?\n\n## Decision\n\nWhat did we decide to do?\n\n## Consequences\n\nWhat becomes easier or harder as a result?\n`;

const ADR_STATUS_LABEL: Record<AdrStatus, string> = {
  proposed: 'Proposed',
  accepted: 'Accepted',
  superseded: 'Superseded',
};

const ADR_STATUS_BADGE: Record<AdrStatus, string> = {
  proposed: 'badge-ghost',
  accepted: 'badge-success',
  superseded: 'badge-warning',
};

// The non-status fields of adrMeta, kept around so saving a status change (or an unrelated field)
// doesn't wipe them — the upsert route replaces adrMeta wholesale, it doesn't merge.
const adrMetaRestOf = (meta?: AdrMeta): Omit<AdrMeta, 'status'> => ({
  number: meta?.number,
  supersededBy: meta?.supersededBy,
  lastVerifiedAt: meta?.lastVerifiedAt,
  driftNote: meta?.driftNote,
  repoUrl: meta?.repoUrl,
});

// Full-page view/edit for a single knowledge doc. There's no single-doc GET endpoint in the REST
// contract (only list-by-project) — fetches the project's full list and finds the matching id,
// same tradeoff the BE ticket left the FE with, not worth a new endpoint for.
//
// docId === 'new' is create mode: skips the fetch, opens straight into the edit form, and
// redirects to the real doc's own URL once saved — same page handles view, edit, and create
// rather than a separate modal (MVPB-53 feedback: needed a real page, and a way to just *read* a
// doc without clicking Edit first).
export default function KnowledgeDocPage() {
  const { projectId, docId } = useParams<{ projectId: string; docId: string }>();
  const navigate = useNavigate();
  const project = useProjectById(projectId);
  const { on, off } = useSocket(auth.currentUser?.uid || '');
  const isNew = docId === 'new';

  const [doc, setDoc] = useState<ProjectKnowledge | null>(null);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>(isNew ? 'edit' : 'view');
  const [isDeleting, setIsDeleting] = useState(false);

  const [title, setTitle] = useState('');
  const [type, setType] = useState<ProjectKnowledgeType>('guide');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ADR-only. `adrStatus` is the one field the user can actually edit; the rest of adrMeta
  // (number/supersededBy/lastVerifiedAt/driftNote) is owned by the drift-check job and the repo
  // sync — kept around in `adrMetaRest` purely so saving doesn't wipe it (the upsert route
  // replaces adrMeta wholesale, it doesn't merge).
  const [adrStatus, setAdrStatus] = useState<AdrStatus>('proposed');
  const [adrMetaRest, setAdrMetaRest] = useState<Omit<AdrMeta, 'status'>>({});

  useEffect(() => {
    if (isNew || !projectId || !docId) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    listProjectKnowledge(projectId)
      .then((docs) => {
        if (cancelled) return;
        const found = docs.find((d) => d._id === docId);
        if (!found) {
          setLoadError('This knowledge doc no longer exists.');
          return;
        }
        setDoc(found);
        setTitle(found.title);
        setType(found.type);
        setContent(found.content);
        setTags(found.tags ?? []);
        setAdrStatus(found.adrMeta?.status ?? 'proposed');
        setAdrMetaRest(adrMetaRestOf(found.adrMeta));
      })
      .catch((err) => !cancelled && setLoadError(knowledgeErrorMessage(err) ?? 'Failed to load this doc.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, docId, isNew]);

  // Live sync — mainly for the "Indexed for AI search" sidebar line, since knowledgeDocUpdated
  // fires again once rag-msa confirms embedding and sets indexedAt (no manual refresh needed to
  // see it flip). Skipped while actively editing so an incoming update can't clobber in-progress
  // form state.
  useEffect(() => {
    if (isNew || !docId) return;
    const onDocUpdated = (data: { userId: string; doc: ProjectKnowledge }) => {
      if (data.doc._id !== docId || mode === 'edit') return;
      setDoc(data.doc);
    };
    on('knowledgeDocUpdated', onDocUpdated);
    return () => off('knowledgeDocUpdated', onDocUpdated as (...args: unknown[]) => void);
  }, [docId, isNew, mode, on, off]);

  const listTo = `/dashboard/projects/${projectId}/knowledge`;

  const startEdit = () => {
    if (!doc) return;
    setTitle(doc.title);
    setType(doc.type);
    setContent(doc.content);
    setTags(doc.tags ?? []);
    setAdrStatus(doc.adrMeta?.status ?? 'proposed');
    setAdrMetaRest(adrMetaRestOf(doc.adrMeta));
    setSaveError(null);
    setMode('edit');
  };

  const cancelEdit = () => {
    if (isNew) {
      navigate(listTo);
      return;
    }
    setSaveError(null);
    setMode('view');
  };

  const addTag = () => {
    const t = tagDraft.trim();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagDraft('');
  };

  const handleSave = async () => {
    if (!projectId || !title.trim() || !content.trim()) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const saved = await upsertProjectKnowledge({
        _id: doc?._id,
        projectId,
        type,
        title: title.trim(),
        content: content.trim(),
        tags: tags.length > 0 ? tags : undefined,
        adrMeta: type === 'adr' ? { ...adrMetaRest, status: adrStatus } : undefined,
      });
      if (isNew) {
        navigate(`/dashboard/projects/${projectId}/knowledge/${saved._id}`, { replace: true });
        return;
      }
      setDoc(saved);
      setMode('view');
    } catch (err) {
      setSaveError(knowledgeErrorMessage(err) ?? 'Failed to save doc.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!doc) return;
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    setIsDeleting(true);
    try {
      await deleteProjectKnowledge(doc._id);
      navigate(listTo);
    } catch (err) {
      setSaveError(knowledgeErrorMessage(err) ?? 'Failed to delete doc.');
      setIsDeleting(false);
    }
  };

  return (
    <TaskDetailShell
      backTo={listTo}
      backLabel="Back to knowledge"
      provider={project?.provider ?? 'standalone'}
      isLoading={isLoading}
      error={loadError}
      headerActions={
        !isNew &&
        doc &&
        mode === 'view' && (
          <>
            <button className="btn btn-ghost btn-sm gap-1.5" onClick={startEdit}>
              <LuPencilLine size={14} />
              Edit
            </button>
            <button className="btn btn-ghost btn-sm gap-1.5 text-error" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <LuLoaderPinwheel className="animate-spin" size={14} /> : <LuTrash2 size={14} />}
              Delete
            </button>
          </>
        )
      }
      sidebar={
        doc && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-2">
              <span className="label-text text-xs">Details</span>
              <div className="text-xs opacity-60 flex flex-col gap-1.5">
                <div>Created {new Date(doc.createdAt).toLocaleString()}</div>
                <div>Updated {new Date(doc.updatedAt).toLocaleString()}</div>
                {doc.type === 'guide' && (
                  <div className="flex items-center gap-1">
                    <LuSparkles size={12} />
                    {doc.indexedAt
                      ? `Indexed for AI search ${new Date(doc.indexedAt).toLocaleDateString()}`
                      : 'Not yet indexed for AI search'}
                  </div>
                )}
                {doc.type === 'adr' && (
                  <div>
                    {doc.adrMeta?.lastVerifiedAt != null
                      ? `Drift-checked ${formatRelativeTime(doc.adrMeta.lastVerifiedAt)}`
                      : 'Never drift-checked yet'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }
    >
      {saveError && (
        <div className="alert alert-error py-2">
          <span className="text-sm">{saveError}</span>
        </div>
      )}

      {mode === 'view' && doc && (
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-5 gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`badge gap-1 ${TYPE_META[doc.type].badgeClass}`}>
                {React.createElement(TYPE_META[doc.type].Icon, { size: 11 })}
                {TYPE_META[doc.type].label}
              </span>
              {doc.type === 'adr' && doc.adrMeta && (
                <>
                  <span className={`badge badge-sm ${ADR_STATUS_BADGE[doc.adrMeta.status]}`}>
                    {ADR_STATUS_LABEL[doc.adrMeta.status]}
                  </span>
                  {doc.adrMeta.number != null && (
                    <span className="badge badge-outline badge-sm font-mono">
                      ADR-{String(doc.adrMeta.number).padStart(4, '0')}
                    </span>
                  )}
                  {doc.adrMeta.repoUrl && (
                    <span className="badge badge-outline badge-sm font-mono">{doc.adrMeta.repoUrl}</span>
                  )}
                </>
              )}
              {doc.tags?.map((t) => (
                <span key={t} className="badge badge-ghost badge-sm">
                  {t}
                </span>
              ))}
            </div>
            {doc.type === 'adr' && !!doc.adrMeta?.driftNote && (
              <div className="alert alert-warning py-2">
                <LuTriangleAlert size={16} className="shrink-0" />
                <span className="text-sm">
                  <b>Drift detected:</b> {doc.adrMeta.driftNote}
                </span>
              </div>
            )}
            <h1 className="text-xl font-semibold">{doc.title}</h1>
            <div className="pt-1">
              <MarkdownRenderer markdown={doc.content} />
            </div>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-5 gap-3">
            <input
              className="input input-bordered input-lg w-full text-base font-semibold"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <div className="form-control">
              <span className="label-text text-xs mb-1.5">Type</span>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(TYPE_META) as ProjectKnowledgeType[]).map((t) => {
                  const meta = TYPE_META[t];
                  const active = type === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setType(t);
                        // Pre-fill the required section structure — only for a brand-new, still-
                        // empty doc, so switching type on an existing one never clobbers content.
                        if (t === 'adr' && isNew && !content.trim()) setContent(ADR_CONTENT_TEMPLATE);
                      }}
                      className={`rounded-lg px-3 py-2 text-left transition ${active ? 'bg-base-200' : 'hover:bg-base-200/60'}`}
                      style={active ? { boxShadow: `inset 0 0 0 1.5px ${meta.color}` } : undefined}
                    >
                      <div className="flex items-center gap-1.5">
                        <meta.Icon size={14} style={{ color: meta.color }} />
                        <span className="text-sm font-medium">{meta.label}</span>
                      </div>
                      <div className="text-xs opacity-50 mt-0.5">{meta.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {type === 'adr' && (
              <div className="form-control max-w-xs">
                <span className="label-text text-xs mb-1.5">Status</span>
                <select
                  className="select select-bordered select-sm"
                  value={adrStatus}
                  onChange={(e) => setAdrStatus(e.target.value as AdrStatus)}
                >
                  {(Object.keys(ADR_STATUS_LABEL) as AdrStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {ADR_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
                {adrMetaRest.number != null && (
                  <span className="text-xs opacity-50 mt-1">
                    ADR-{String(adrMetaRest.number).padStart(4, '0')} · number/drift status are set by the sync and
                    drift-check jobs, not editable here.
                  </span>
                )}
              </div>
            )}

            <div className="form-control">
              <span className="label-text text-xs mb-1.5">Content — Markdown supported</span>
              <MarkdownField value={content} onChange={setContent} placeholder="What should the AI know?" rows={12} />
              {type === 'adr' && (
                <span className="text-xs opacity-50 mt-1">
                  Must include ## Context, ## Decision, and ## Consequences sections.
                </span>
              )}
            </div>

            <div className="form-control">
              <span className="label-text text-xs mb-1.5">Tags (optional)</span>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map((t) => (
                  <span key={t} className="badge badge-outline gap-1">
                    {t}
                    <button
                      type="button"
                      onClick={() => setTags((prev) => prev.filter((x) => x !== t))}
                      aria-label={`Remove tag ${t}`}
                    >
                      <LuX size={11} />
                    </button>
                  </span>
                ))}
              </div>
              <input
                className="input input-bordered input-sm w-full max-w-xs"
                placeholder="Add a tag and press Enter…"
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                onBlur={addTag}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={isSaving}>
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={isSaving || !title.trim() || !content.trim()}
              >
                {isSaving && <LuLoaderPinwheel className="animate-spin" size={14} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </TaskDetailShell>
  );
}
